import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { removeAuthorization } from "../../Functions/AuthFunction";
import { useGlobalContext } from "../../ContextApi/GlobalContext";

const sidebarVariants = {
  open: { x: 0, transition: { type: "spring", stiffness: 300, damping: 30 } },
  closed: {
    x: "-100%",
    transition: { type: "spring", stiffness: 300, damping: 30 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: (i) => ({
    opacity: 1,
    x: 0,
    transition: { delay: i * 0.05, duration: 0.3 },
  }),
};

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { dispatch, state } = useGlobalContext();

  const user = state.companyData || {};
  const userName = user.name || "Admin User";
  const userRole = user.role || "Administrator";
  const userInitial = userName.charAt(0).toUpperCase();
  const profilePicURL = user.profilePicURL || "";

  const navItems = [
    { link: "/dashboard", title: "Dashboard", icon: "dashboard" },
    { link: "/forms", title: "My Forms", icon: "description" },
    { link: "/queries", title: "Queries", icon: "inbox_customize" },
    { link: "/notes", title: "Notes", icon: "sticky_note_2" },
    { link: "/myprofile", title: "My Profile", icon: "account_circle" },
    { link: "/support", title: "Help & Support", icon: "support_agent" },
    { link: "/plans", title: "Plans", icon: "monetization_on" },
  ];

  const handleListItemClick = (link, title) => {
    if (typeof window !== "undefined" && window.innerWidth < 1024) {
      dispatch({ sideBar: false });
    }
    dispatch({ pageLink: link, pageName: title });
    navigate(link);
  };

  const logoutHandler = () => {
    removeAuthorization();
    dispatch({ authToken: false, companyData: false, dashboardData: false });
    navigate("/login");
  };

  const isOpen = Boolean(state.sideBar);

  return (
    <>
      {/* Mobile backdrop */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => dispatch({ sideBar: false })}
            className="fixed inset-0 bg-[#0B1C2D]/80 backdrop-blur-sm z-[999] lg:hidden"
            aria-hidden="true"
          />
        )}
      </AnimatePresence>

      {/*
        Mobile: off-canvas drawer (animate x).
        Desktop: always visible at 280px (existing expanded design).
      */}
      <motion.aside
        initial={false}
        animate={isOpen ? "open" : "closed"}
        variants={sidebarVariants}
        className="fixed top-0 left-0 z-[1000] h-screen w-[280px] max-w-[85vw] flex flex-col bg-[#0B1C2D] border-r border-white/5 shadow-2xl
          lg:!translate-x-0 lg:w-[280px] lg:max-w-none"
        aria-label="Main navigation"
      >
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
          <div className="absolute -top-[10%] -left-[10%] w-64 h-64 bg-[#E63946]/5 rounded-full blur-[80px]" />
          <div className="absolute bottom-0 right-0 w-64 h-64 bg-[#1E3A8A]/5 rounded-full blur-[80px]" />
        </div>

        <div className="relative z-10 h-24 flex items-center justify-between px-6">
          <div
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-3 group cursor-pointer"
          >
            <div className="flex items-center justify-center size-10 rounded-xl bg-gradient-to-br from-[#E63946] to-[#D62839] text-white shadow-lg shadow-[#E63946]/20 transition-transform duration-300 group-hover:scale-105">
              <span className="material-symbols-outlined text-[24px]">hub</span>
            </div>
            <div>
              <span className="block text-lg font-bold tracking-tight text-white leading-none group-hover:text-gray-100 transition-colors">
                ContactHQ
              </span>
              <span className="text-[10px] text-gray-500 font-medium tracking-wider uppercase">
                Workspace
              </span>
            </div>
          </div>

          <button
            type="button"
            aria-label="Close menu"
            onClick={() => dispatch({ sideBar: false })}
            className="lg:hidden text-gray-400 hover:text-white transition-colors p-1 rounded-md hover:bg-white/10"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <nav className="relative z-10 flex-1 overflow-y-auto py-4 px-4 space-y-1.5 custom-scrollbar">
          {navItems.map((item, index) => {
            const isActive =
              location.pathname === item.link ||
              (item.link !== "/dashboard" &&
                location.pathname.startsWith(item.link));

            return (
              <motion.button
                key={item.link}
                type="button"
                custom={index}
                initial="hidden"
                animate="visible"
                variants={itemVariants}
                onClick={() => handleListItemClick(item.link, item.title)}
                className={`group w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 relative overflow-hidden
                  ${
                    isActive
                      ? "text-white shadow-lg shadow-[#E63946]/10"
                      : "text-gray-400 hover:bg-white/5 hover:text-white"
                  }
                `}
              >
                {isActive && (
                  <div className="absolute inset-0 bg-gradient-to-r from-[#E63946] to-[#B91C1C] opacity-90" />
                )}

                <span
                  className={`material-symbols-outlined relative z-10 text-[22px] transition-transform duration-300 
                  ${isActive ? "text-white" : "group-hover:scale-110"}`}
                >
                  {item.icon}
                </span>

                <span className="relative z-10 flex-1 text-left tracking-wide">
                  {item.title}
                </span>
              </motion.button>
            );
          })}
        </nav>

        <div className="relative z-10 p-4 border-t border-white/5 bg-[#081522]/50 backdrop-blur-sm">
          <div
            className="flex items-center gap-3 mb-4 p-3 rounded-xl bg-white/5 border border-white/5 hover:bg-white/10 transition-colors cursor-pointer"
            onClick={() => {
              if (window.innerWidth < 1024) dispatch({ sideBar: false });
              navigate("/myprofile");
            }}
          >
            <div className="size-9 rounded-full bg-gradient-to-tr from-blue-500 to-cyan-400 flex items-center justify-center text-white font-bold text-sm shadow-inner overflow-hidden">
              {profilePicURL ? (
                <img
                  src={profilePicURL}
                  alt=""
                  className="w-full h-full object-cover rounded-full"
                />
              ) : (
                userInitial
              )}
            </div>
            <div className="flex-1 overflow-hidden">
              <p className="text-sm font-semibold text-white truncate">
                {userName}
              </p>
              <p className="text-[11px] text-gray-400 truncate">{userRole}</p>
            </div>
            <span className="material-symbols-outlined text-gray-500 text-[18px]">
              chevron_right
            </span>
          </div>

          <button
            type="button"
            onClick={logoutHandler}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-xs font-semibold text-gray-400 hover:text-[#E63946] hover:bg-[#E63946]/10 transition-all duration-200 border border-transparent hover:border-[#E63946]/20"
          >
            <span className="material-symbols-outlined text-[18px]">logout</span>
            <span>Sign Out</span>
          </button>
        </div>
      </motion.aside>
    </>
  );
};

export default Sidebar;
